import { ScaleTime, ScaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { curveMonotoneX, line } from 'd3-shape';
import type { Theme } from '@mui/material';

import type { ConditionRating } from 'types/conditionRating';
import type { LiveWindPoint } from 'types/liveWind';
import { conditionColorMap } from 'utils/colors';

import getDashArray from './calculateStrokeDashArray';

import styles from './LiveWindLineGraph.module.scss';

export const ratingsToGradientStops = (ratings: Array<ConditionRating>) => {
  const total = ratings.length;
  return ratings.map((r, i) => ({
    offset: `${(i / total) * 100}%`,
    color: conditionColorMap.get(r?.rating ?? '')?.default,
  }));
};

export const getCurrentTimeDotInterpolatedData = (
  data: Array<LiveWindPoint>,
  currentMarkerXPercentage = 0,
) => {
  try {
    if (currentMarkerXPercentage === 0) {
      return {
        speed: data[0].wind.speed,
        timestamp: data[0].timestamp,
      };
    }
    if (currentMarkerXPercentage === 1) {
      return {
        speed: data[data.length - 1].wind.speed,
        timestamp: data[data.length - 1].timestamp,
      };
    }
    const timestamp = Math.round(+new Date() / 1000);

    const beforeIndex = Math.floor((data.length - 1) * currentMarkerXPercentage);
    const afterIndex = Math.ceil((data.length - 1) * currentMarkerXPercentage);

    const beforePoint = data[beforeIndex];
    const afterPoint = data[afterIndex];

    const beforePointCloser = afterPoint.timestamp - timestamp >= timestamp - beforePoint.timestamp;

    const t0 = beforePointCloser ? beforePoint : afterPoint;
    const t1 = beforePointCloser ? afterPoint : beforePoint;

    const timespan = Math.abs(timestamp - t0.timestamp);
    const speedIncrement = t1.wind.speed - t0.wind.speed;
    const timeIncrement = Math.abs(t1.timestamp - t0.timestamp);
    const interpolatedSpeed = t0.wind.speed + (timespan * speedIncrement) / timeIncrement;

    return {
      timestamp,
      speed: interpolatedSpeed,
    };
  } catch (_error) {
    return {
      timestamp: data[0].timestamp,
      speed: data[0].wind.speed,
    };
  }
};

export const setupLiveWindGraph = (
  x: ScaleTime<number, number>,
  y: ScaleLinear<number, number>,
  data: Array<LiveWindPoint>,
  ratings: Array<ConditionRating>,
  theme: Theme,
  currentMarkerXPercentage: number,
) => {
  const graphSvg = select<HTMLDivElement, LiveWindPoint>(`.${styles.graphSvg}`);

  const path = line<LiveWindPoint>()
    .x((d) => x(d.timestamp))
    .y((d) => y(d.wind.speed))
    .curve(curveMonotoneX);

  graphSvg
    .append('linearGradient')
    .attr('id', 'conditions-gradient')
    .attr('gradientUnits', 'userSpaceOnUse')
    .attr('x1', x(data[0].timestamp))
    .attr('y1', 0)
    .attr('y2', 0)
    .attr('x2', x(data[data.length - 1].timestamp))
    .selectAll('stop')
    .data(ratingsToGradientStops(ratings))
    .enter()
    .append('stop')
    .attr('offset', (d) => d.offset)
    .attr('stop-color', (d) => d?.color ?? '');

  graphSvg
    .append('path')
    .data(data)
    .attr('class', styles.liveWindLine)
    .attr('d', path(data))

    .attr('stroke-dasharray', function () {
      return getDashArray(data, this, x);
    })
    .attr('stroke', 'url(#conditions-gradient)');

  const currentTimeDot = select(`.${styles.graphSvg}`)
    .append('g')
    .attr('class', styles.currentTimeDot);
  currentTimeDot
    .append('circle')
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('r', 8)
    .attr('fill', theme.palette.secondary.main)
    .style('opacity', 0.2);
  currentTimeDot
    .append('circle')
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('r', 4)
    .attr('fill', theme.palette.secondary.main);

  const dotData = getCurrentTimeDotInterpolatedData(data, currentMarkerXPercentage);

  currentTimeDot.style('transform', `translate(${x(dotData.timestamp)}px, ${y(dotData.speed)}px)`);
};
